// Tap-vs-hold disambiguation for the card-inspector hotkey (Space).
//
// Three interaction modes coexist:
//   - Hover then tap Space: opens inspector and leaves it open. Tap
//     again to close.
//   - Hover then hold Space: opens inspector. Release closes it.
//   - Hold Space first, hover after: arms a pending open that lives
//     only while Space is down. A hover-enter during the hold opens
//     the inspector; release closes it (if held past the threshold).
//     Releasing Space without ever hovering a card disarms. A stray
//     tap must not open the inspector on whatever card the cursor
//     crosses next, minutes later.
//
// We can't tell tap vs hold on keydown. We decide on keyup based on
// how long Space was held. Anything below the threshold counts as a
// tap; at or above, as a hold.

export const INSPECTOR_HOLD_THRESHOLD_MS = 200;

export class InspectorHoldTracker {
  constructor() {
    this._keyDownAt = 0;
    // Set by every keydown that doesn't close the inspector. Consumed
    // by shouldCloseOnKeyUp to decide hold-mode close on release.
    this._holdPending = false;
    // Set when Space went down without a card hovered. Consumed by the
    // next hover-enter to open the inspector.
    this._pending = false;
  }

  // Inspector just opened (hover was present at keydown).
  noteOpen() {
    this._keyDownAt = Date.now();
    this._holdPending = true;
    this._pending = false;
  }

  // Space went down with no card hovered. Arm so the next hover-enter
  // opens the inspector. We still record the keydown timestamp so the
  // hold-mode close on release works if a hover happens before keyup.
  noteArmed() {
    this._keyDownAt = Date.now();
    this._holdPending = true;
    this._pending = true;
  }

  // Hover-enter handlers call this on every change. Returns true once
  // when the inspector should open from a previously-armed state, then
  // clears the pending flag so we don't re-open on subsequent hovers.
  consumePending() {
    if (!this._pending) return false;
    this._pending = false;
    return true;
  }

  // Tap-toggle close, or any other explicit "cancel armed state" path.
  noteClose() {
    this._keyDownAt = 0;
    this._holdPending = false;
    this._pending = false;
  }

  // On Space keyup, decide whether to close the inspector. Returns
  // true only when the press was long enough to count as a hold.
  // Always disarms: the armed state does not outlive the key press.
  shouldCloseOnKeyUp() {
    this._pending = false;
    if (!this._holdPending) return false;
    this._holdPending = false;
    return Date.now() - this._keyDownAt >= INSPECTOR_HOLD_THRESHOLD_MS;
  }
}
